import { useEffect, useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { Image } from '../../types/entities/image.entity';
import { isGalleryVideo } from '../../utils/galleryMedia';

interface GallerySliderViewProps {
    images: Image[];
    hasMore: boolean;
    isFetching: boolean;
    onDelete: (image: Image) => void;
    onLoadMore: () => void;
}

export const GallerySliderView = ({
    images,
    hasMore,
    isFetching,
    onDelete,
    onLoadMore,
}: GallerySliderViewProps) => {
    const galleryRef = useRef<any>(null);

    const stopAllVideos = () => {
        document.querySelectorAll('.gallery-slider-video').forEach((video) => {
            (video as HTMLVideoElement).pause();
            (video as HTMLVideoElement).currentTime = 0;
        });
    };

    useEffect(() => {
        if (images.length > 0 && hasMore && images.length <= 1 && !isFetching) {
            onLoadMore();
        }
    }, [images.length, hasMore, isFetching, onLoadMore]);

    const galleryItems = images.map((image) => {
        if (isGalleryVideo(image)) {
            return {
                original: image.preview ?? '',
                thumbnail: image.preview ?? '',
                renderItem: () => (
                    <Box position="relative" width="100%" height="100%">
                        <video
                            className="gallery-slider-video"
                            controls
                            style={{ width: '100%', maxHeight: 500 }}
                            src={image.path}
                            poster={image.preview ?? ''}
                        />
                        <IconButton
                            onClick={() => onDelete(image)}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                color: 'error.main',
                                background: 'rgba(0,0,0,0.5)',
                                zIndex: 2,
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                ),
                description: '',
            };
        }

        return {
            original: image.path,
            thumbnail: image.path,
            renderItem: () => (
                <Box position="relative" width="100%" height="100%">
                    <img
                        src={image.path}
                        alt=""
                        style={{ width: '100%', maxHeight: 500, objectFit: 'contain' }}
                        loading="lazy"
                    />
                    <IconButton
                        onClick={() => onDelete(image)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: 'error.main',
                            background: 'rgba(0,0,0,0.5)',
                            zIndex: 2,
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            ),
            description: '',
        };
    });

    return (
        <Box sx={{ width: '100%', mb: 4 }}>
            <ImageGallery
                ref={galleryRef}
                items={galleryItems}
                showPlayButton={false}
                showFullscreenButton
                showThumbnails
                showBullets
                lazyLoad
                additionalClass="gallery-slider-fullscreen-fix"
                useBrowserFullscreen={false}
                onSlide={(index) => {
                    stopAllVideos();
                    if (index === images.length - 1 && hasMore && !isFetching) {
                        onLoadMore();
                    }
                }}
            />
        </Box>
    );
};
